import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    ArrowLeft2,
    Box,
    DocumentText,
    Layer,
    Profile2User,
    TickSquare,
} from "iconsax-react";

const METRIC_TILES = [
    {
        id: "items",
        countKey: "itemCount",
        labelKey: "setup.items",
        href: "/dashboard/exp-settings",
        Icon: Box,
    },
    {
        id: "itemtypes",
        countKey: "itemTypeCount",
        labelKey: "setup.itemtypes",
        href: "/dashboard/exp-settings",
        Icon: Layer,
    },
    {
        id: "forms",
        countKey: "formCount",
        labelKey: "setup.forms",
        href: "/dashboard/exp-settings",
        Icon: DocumentText,
    },
];

function StageRow({ stage }) {
    const { t } = useTranslation();
    const detail =
        stage.total != null
            ? `${stage.count}/${stage.total}`
            : String(stage.count ?? 0);

    return (
        <li className="setup-stage">
            <span
                className={`setup-stage-mark ${
                    stage.complete ? "is-done" : ""
                }`}
                aria-hidden="true"
            >
                {stage.complete ? <TickSquare size={16} variant="Bold" /> : null}
            </span>
            <div className="setup-stage-copy">
                <span className="setup-stage-title">
                    {t(stage.labelKey)}
                </span>
                <span className="setup-stage-meta">
                    {stage.complete
                        ? t("setup.done")
                        : t("setup.count.value", { count: detail })}
                </span>
            </div>
            <Link
                className="setup-stage-link"
                to={stage.href}
                aria-label={`${t("setup.cta.complete")}: ${t(stage.labelKey)}`}
            >
                {stage.complete ? t("setup.cta.open") : t("setup.cta.complete")}
                <ArrowLeft2 size={14} aria-hidden="true" />
            </Link>
        </li>
    );
}

function SetupChecklist({ stages, showProgress, percent, completedCount, totalStages }) {
    const { t } = useTranslation();
    if (!stages.length) return null;

    return (
        <div className="setup-checklist">
            {showProgress ? (
                <div className="setup-progress">
                    <div className="setup-progress-copy">
                        <span className="setup-progress-label">
                            {t("setup.progress.title")}
                        </span>
                        <span className="setup-progress-value">
                            {t("setup.progress.percent", { percent })}
                        </span>
                    </div>
                    <div
                        className="setup-progress-track"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={percent}
                        aria-label={t("setup.progress.title")}
                    >
                        <div
                            className="setup-progress-fill"
                            style={{ width: `${percent}%` }}
                        />
                    </div>
                    <span className="setup-progress-hint">
                        {t("setup.progress.steps", {
                            done: completedCount,
                            total: totalStages,
                        })}
                    </span>
                </div>
            ) : null}
            <ul className="setup-stage-list">
                {stages.map((stage) => (
                    <StageRow key={stage.id} stage={stage} />
                ))}
            </ul>
        </div>
    );
}

export default function ChartFallback({ setup, variant = "metrics", stageIds }) {
    const { t } = useTranslation();
    const filtered = Array.isArray(stageIds)
        ? setup.stages.filter((stage) => stageIds.includes(stage.id))
        : setup.stages;
    const stages = filtered.length ? filtered : setup.stages;

    return (
        <div className={`chart-fallback chart-fallback--${variant}`} role="status">
            <p className="chart-fallback-kicker">{t("setup.chart.kicker")}</p>
            <p className="chart-fallback-desc">{t("setup.chart.hint")}</p>
            {setup.loading ? (
                <div className="chart-fallback-loading">{t("loading")}</div>
            ) : variant === "metrics" ? (
                <div className="setup-metrics">
                    {METRIC_TILES.map((tile) => (
                        <Link
                            key={tile.id}
                            className="setup-metric"
                            to={tile.href}
                        >
                            <span className="setup-metric-icon" aria-hidden="true">
                                <tile.Icon size={18} />
                            </span>
                            <span className="setup-metric-value">
                                {setup[tile.countKey] || 0}
                            </span>
                            <span className="setup-metric-label">
                                {t(tile.labelKey)}
                            </span>
                        </Link>
                    ))}
                </div>
            ) : variant === "participation" ? (
                <SetupChecklist
                    stages={
                        stages.length
                            ? stages
                            : [
                                  {
                                      id: "profile",
                                      labelKey: "setup.stage.profile",
                                      complete: false,
                                      href: "/dashboard/profile",
                                      count: setup.profileDone,
                                      total: 5,
                                  },
                              ]
                    }
                    showProgress={false}
                />
            ) : (
                <SetupChecklist
                    stages={stages}
                    showProgress
                    percent={setup.percent}
                    completedCount={setup.completedCount}
                    totalStages={setup.totalStages}
                />
            )}
            {variant === "metrics" && !setup.loading ? (
                <p className="chart-fallback-footnote">
                    <Profile2User size={14} aria-hidden="true" />
                    {t("setup.metrics.footnote")}
                </p>
            ) : null}
        </div>
    );
}
